/**
 * « Stickers & icônes » and « Icônes » — the editor's resources panel
 * (AlbumEditorPage → ElementsSidebar, customer AND operator editor, side panel
 * on desktop, bottom sheet on a phone).
 *
 * FAST AND SMOOTH (owner 2026-10-14):
 *   · tiles are small files made ahead (256 px stickers ≤ 20 KB, 200 px cover
 *     icons ~9 KB) — never the print originals, never an image transform;
 *   · fixed square tiles with width/height on the <img> (no layout shift),
 *     `loading="lazy"` + `decoding="async"`;
 *   · a grid renders 36 tiles, then 36 more each time its end comes near
 *     (IntersectionObserver on a sentinel; « Afficher plus » as a fallback) —
 *     no nested scroll box: the panel / sheet is the only scroller;
 *   · pressing a sticker starts fetching the canvas copy (med) so it is
 *     usually there when the tap lands.
 * Tap = add to the active page (the parent builds the element:
 * lib/stickers/catalog.ts stickerElement).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Cover, EditorIcon } from '../../lib/api';
import { SafeSvg } from '../SafeSvg';
import {
  STICKER_THEMES, STICKER_TYPES, filterStickers, loadStickerData, stickerDataNow, stickerMedUrl, stickerThumbUrl, typesIn,
  type Sticker, type StickerData, type StickerType,
} from '../../lib/stickers/catalog';
import { iconTile, sortIconsByDestination } from '../../lib/coverIcons';
import { useTileDrag } from '../../lib/tileDrag';

// The editor's chrome tokens (AlbumEditorPage `UI`) — CSS variables, so night mode works here too.
const INK = 'var(--color-ed-ink)';
const MUTED = 'var(--color-adm-muted)';
const FAINT = 'var(--color-adm-faint)';
const GOLD = '#C9A24A';
/** Inside a light island the ink is the editor's own (COLORS.dark), as in light mode. */
const DARK_INK = '#1F2937';
/** A sticker tile: a touch darker than the cream page so white labels and tapes still read. */
const STICKER_TILE = '#F1ECE2';
const ICON_TILE = '#FAF6EC';

const CHUNK = 36;
/** « Icônes » shows this many (or every destination match) until « Voir les N icônes ». */
const ICONS_FIRST_LOOK = 12;

/** The catalogue + cover-thumb list, loaded once on first use (own chunks). */
function useStickerData(): StickerData | null {
  const [data, setData] = useState<StickerData | null>(stickerDataNow);
  useEffect(() => {
    if (data) return;
    let off = false;
    loadStickerData().then((d) => { if (!off) setData(d); }).catch(() => { /* panel shows no stickers */ });
    return () => { off = true; };
  }, [data]);
  return data;
}

/** The nearest ancestor that scrolls vertically (null = the window). */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let e = el.parentElement; e; e = e.parentElement) {
    const oy = getComputedStyle(e).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && e.scrollHeight > e.clientHeight) return e;
  }
  return null;
}

/** Renders `items` CHUNK at a time; the next chunk mounts as its end nears. */
/** (Give it a `key` per filter: a new filter starts again from the first chunk.) */
function ChunkedGrid<T>({ items, className, render }: {
  items: readonly T[];
  className: string;
  render: (item: T, index: number) => ReactNode;
}) {
  const [limit, setLimit] = useState(CHUNK);
  const sentinel = useRef<HTMLDivElement | null>(null);
  const more = items.length > limit;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !more || typeof IntersectionObserver === 'undefined') return;
    // The panel / bottom sheet is the scroller: observe in IT, so the margin
    // loads the next chunk ~a screen before its end (the window would clip it).
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setLimit((l) => l + CHUNK);
    }, { root: scrollParent(el), rootMargin: '400px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [more, limit]);
  return (
    <>
      <div className={className}>{items.slice(0, limit).map(render)}</div>
      {more && (
        <div ref={sentinel} className="pt-2 text-center">
          <button
            type="button"
            onClick={() => setLimit((l) => l + CHUNK)}
            className="text-[11px] px-3 py-1 rounded-full"
            style={{ color: MUTED, border: `1px solid ${GOLD}30` }}
          >
            Afficher plus ({items.length - limit})
          </button>
        </div>
      )}
    </>
  );
}

function Chip({ active, onClick, children, title }: { active: boolean; onClick: () => void; children: ReactNode; title?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className="shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap"
      style={{ background: active ? GOLD : `${GOLD}15`, color: active ? '#FFFFFF' : INK }}
    >
      {children}
    </button>
  );
}

const warmed = new Set<string>();
/** Start fetching the canvas copy on press, so the tap's element draws at once. */
function warm(url: string) {
  if (warmed.has(url) || typeof Image === 'undefined') return;
  warmed.add(url);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
}

export interface StickersSectionProps {
  /** The album's theme (cover categories / the page's model) — its chip comes first and opens. */
  albumTheme: string | null;
  onAddSticker: (sticker: Sticker) => void;
  /** Admin-managed SVG icons (Paramètres → Ressources de l'éditeur). */
  adminIcons: EditorIcon[];
  adminIconsLoading: boolean;
  onInsertIcon: (icon: EditorIcon) => void;
}

/** « Stickers & icônes »: the decor stickers (by theme and kind) and the admin's icons. */
export function StickersSection({ albumTheme, onAddSticker, adminIcons, adminIconsLoading, onInsertIcon }: StickersSectionProps) {
  const data = useStickerData();
  const [theme, setTheme] = useState<string>(albumTheme ?? 'all');
  const [type, setType] = useState<StickerType | 'all'>('all');
  const [query, setQuery] = useState('');
  // The album's theme can arrive after mount (cover loaded): follow it until the client picks a chip.
  const picked = useRef(false);
  useEffect(() => { if (!picked.current && albumTheme) setTheme(albumTheme); }, [albumTheme]);

  const all = useMemo(() => data?.stickers ?? [], [data]);
  const searching = query.trim().length > 0;
  // Type chips: the kinds the chosen theme (or the search) has.
  const scope = useMemo(() => filterStickers(all, { theme, type: 'all', query }), [all, theme, query]);
  const types = useMemo(() => typesIn(scope), [scope]);
  const effectiveType = type !== 'all' && !types.includes(type) ? 'all' : type;
  const shown = useMemo(() => filterStickers(all, { theme, type: effectiveType, query }), [all, theme, effectiveType, query]);

  const q = query.trim().toLowerCase();
  const icons = useMemo(() => (q
    ? adminIcons.filter((i) => i.name.toLowerCase().includes(q) || i.tags.some((t) => t.toLowerCase().includes(q)))
    : adminIcons), [adminIcons, q]);

  const themeChips = useMemo(() => {
    const first = albumTheme ? STICKER_THEMES.filter((t) => t.slug === albumTheme) : [];
    return [...first, { slug: 'all', label: 'Tous' }, ...STICKER_THEMES.filter((t) => t.slug !== albumTheme)];
  }, [albumTheme]);

  const nothingAtAll = !!data && all.length === 0 && !adminIconsLoading && adminIcons.length === 0;
  // DRAG AND DROP (owner 2026-10-20) — press and move a tile (long press on a
  // phone) to drop it exactly where it goes; a tap still adds it (lib/tileDrag).
  const drag = useTileDrag();

  return (
    <div className="space-y-2" data-sticker-panel="">
      <p className="text-[11px]" style={{ color: MUTED }}>
        Touchez un sticker pour l'ajouter à la page active, ou glissez-le où vous voulez.
      </p>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Rechercher (fleur, avion, cœur…)"
        aria-label="Rechercher un sticker"
        className="w-full px-3 py-2 text-xs rounded-md border outline-none border-[#E5E7EB] dark:border-adm-line-strong"
      />

      {!searching && (
        <div className="flex gap-1 overflow-x-auto pb-0.5 -mx-0.5 px-0.5" style={{ scrollbarWidth: 'none' }} role="tablist" aria-label="Thèmes">
          {themeChips.map((t) => (
            <Chip key={t.slug} active={theme === t.slug} onClick={() => { picked.current = true; setTheme(t.slug); }}
              title={t.slug === albumTheme ? 'Le thème de votre album' : undefined}>
              {t.slug === albumTheme ? `✦ ${t.label}` : t.label}
            </Chip>
          ))}
        </div>
      )}
      {types.length > 1 && (
        <div className="flex gap-1 overflow-x-auto pb-0.5 -mx-0.5 px-0.5" style={{ scrollbarWidth: 'none' }} aria-label="Types">
          <Chip active={effectiveType === 'all'} onClick={() => setType('all')}>Tout</Chip>
          {STICKER_TYPES.filter((t) => types.includes(t.type)).map((t) => (
            <Chip key={t.type} active={effectiveType === t.type} onClick={() => setType(t.type)}>{t.label}</Chip>
          ))}
        </div>
      )}

      {!data ? (
        <div className="grid grid-cols-4 md:grid-cols-3 gap-1.5" aria-busy="true">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="aspect-square rounded-lg animate-pulse" style={{ background: STICKER_TILE }} />
          ))}
        </div>
      ) : shown.length > 0 ? (
        <ChunkedGrid
          key={`${theme}|${effectiveType}|${q}`}
          items={shown}
          className="grid grid-cols-4 md:grid-cols-3 gap-1.5"
          render={(s) => {
            const d = drag({ kind: 'sticker', sticker: s }, stickerThumbUrl(s));
            return (
            <button
              key={s.id}
              type="button"
              {...d}
              onClick={() => onAddSticker(s)}
              onPointerDown={(e) => { warm(stickerMedUrl(s)); d.onPointerDown(e); }}
              onTouchStart={(e) => { warm(stickerMedUrl(s)); d.onTouchStart(e); }}
              // Night mode: the sticker as it prints — a light island.
              data-adm-media=""
              data-sticker={s.id}
              className="aspect-square rounded-lg overflow-hidden flex items-center justify-center p-1.5 transition-transform active:scale-95"
              style={{ ...d.style, background: STICKER_TILE, color: DARK_INK, border: `1px solid ${GOLD}20` }}
              title={s.name}
              aria-label={`Ajouter le sticker ${s.name}`}
            >
              <img
                src={stickerThumbUrl(s)}
                alt=""
                width={128}
                height={128}
                className="w-full h-full object-contain pointer-events-none select-none"
                draggable={false}
                loading="lazy"
                decoding="async"
              />
            </button>
            );
          }}
        />
      ) : searching ? (
        <p className="text-xs text-center py-2" style={{ color: FAINT }}>Aucun sticker pour « {query.trim()} ».</p>
      ) : null}

      {/* The admin's own icons (Paramètres → Ressources de l'éditeur), alongside. */}
      {icons.length > 0 && (
        <>
          <p className="pt-1 text-[10px] uppercase tracking-wide" style={{ color: MUTED }}>Icônes de l'atelier</p>
          <div className="grid grid-cols-4 md:grid-cols-3 gap-1.5">
            {icons.map((ic) => (
              <button
                key={ic.id}
                type="button"
                {...drag({ kind: 'adminIcon', icon: ic }, null)}
                onClick={() => onInsertIcon(ic)}
                // Night mode: the icon in its print colour — a light island.
                data-adm-media=""
                className="aspect-square rounded-lg border bg-white p-2 flex items-center justify-center hover:shadow transition-all active:scale-95"
                style={{ WebkitTouchCallout: 'none', userSelect: 'none', borderColor: '#E5E7EB', color: '#1F2937' }}
                title={ic.name}
                aria-label={`Insérer l'icône ${ic.name}`}
              >
                <SafeSvg
                  svg={ic.svg}
                  className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full"
                />
              </button>
            ))}
          </div>
        </>
      )}
      {adminIconsLoading && !data && <p className="text-xs text-center py-2" style={{ color: FAINT }}>Chargement…</p>}
      {searching && shown.length === 0 && icons.length === 0 && adminIcons.length > 0 && (
        <p className="text-xs text-center py-1" style={{ color: FAINT }}>Aucun résultat.</p>
      )}
      {nothingAtAll && (
        <p className="text-xs text-center py-4" style={{ color: FAINT }}>
          Aucune icône disponible. Demandez à l'admin d'en ajouter dans Paramètres → Ressources de l'éditeur.
        </p>
      )}
    </div>
  );
}

/** One « Icônes » tile: the small thumb, the old source if the thumb is missing. */
function CoverIconTile({ cover, thumbs, match, onInsert }: {
  cover: Cover; thumbs: Record<string, string> | null; match: boolean; onInsert: (c: Cover) => void;
}) {
  const tile = iconTile(cover, thumbs);
  // The thumb failed (not uploaded yet, a cover changed since) → the old source.
  const [failed, setFailed] = useState<string | null>(null);
  const src = failed && failed === tile.src ? tile.fallback : tile.src;
  const drag = useTileDrag();
  const d = drag({ kind: 'coverIcon', cover }, src ?? null);
  return (
    <button
      type="button"
      {...d}
      onClick={() => onInsert(cover)}
      // Night mode: the icon as it will print — a light island.
      data-adm-media=""
      data-cover-icon={cover.id}
      className="aspect-square rounded-lg overflow-hidden flex items-center justify-center p-1 transition-transform active:scale-95"
      style={{ ...d.style, background: tile.bg ?? ICON_TILE, color: DARK_INK, border: `1px solid ${match ? GOLD : `${GOLD}25`}` }}
      title={cover.name}
      aria-label={`Ajouter l'icône ${cover.name}`}
    >
      <img
        src={src}
        alt=""
        width={100}
        height={100}
        className="w-full h-full object-contain pointer-events-none select-none"
        draggable={false}
        loading="lazy"
        decoding="async"
        onError={() => { if (tile.src && src === tile.src && tile.fallback !== tile.src) setFailed(tile.src); }}
      />
    </button>
  );
}

/** « Icônes »: every cover décor, this album's destination first (✦). */
export function CoverIconsSection({ covers, destination, onInsertCoverIcon }: {
  covers: Cover[]; destination: string; onInsertCoverIcon: (c: Cover) => void;
}) {
  const data = useStickerData();
  const { list, matchCount } = useMemo(() => sortIconsByDestination(covers, destination), [covers, destination]);
  // A first look (the destination's icons first), the rest on demand — so the stickers
  // below stay one short scroll away instead of under 253 tiles.
  const [open, setOpen] = useState(false);
  const firstLook = Math.max(ICONS_FIRST_LOOK, Math.min(matchCount, 2 * ICONS_FIRST_LOOK));
  const items = open ? list : list.slice(0, firstLook);
  if (list.length === 0) return <p className="px-1 text-[10px]" style={{ color: MUTED }}>Aucune icône disponible.</p>;
  return (
    <>
      {matchCount > 0 && (
        <p className="px-1 pb-1 text-[9px] uppercase tracking-wide" style={{ color: GOLD }}>
          ✦ Pour « {destination} »
        </p>
      )}
      {!data ? (
        <div className="grid grid-cols-4 md:grid-cols-3 gap-1.5" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="aspect-square rounded-lg animate-pulse" style={{ background: ICON_TILE }} />)}
        </div>
      ) : (
        <ChunkedGrid
          key={`${destination}|${list.length}|${open}`}
          items={items}
          className="grid grid-cols-4 md:grid-cols-3 gap-1.5"
          render={(c, i) => (
            <CoverIconTile key={c.id} cover={c} thumbs={data.coverThumbs} match={i < matchCount} onInsert={onInsertCoverIcon} />
          )}
        />
      )}
      {!open && list.length > firstLook && (
        <div className="pt-2 text-center">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-[11px] px-3 py-1 rounded-full"
            style={{ color: MUTED, border: `1px solid ${GOLD}30` }}
          >
            Voir les {list.length} icônes
          </button>
        </div>
      )}
    </>
  );
}
